/**
 * Some imported content items store numeric-answer units in caret notation
 * (`"nm^(-3/2)"`, `"m^(-3/2)"`) instead of Unicode superscripts
 * (`"nm⁻³ᐟ²"`). `formatUnit` normalises the former to the latter so a unit
 * always renders the same way wherever it is shown to a student — pure,
 * defensive, never throws.
 */

const SUPERSCRIPT_DIGITS: Record<string, string> = {
  "0": "⁰",
  "1": "¹",
  "2": "²",
  "3": "³",
  "4": "⁴",
  "5": "⁵",
  "6": "⁶",
  "7": "⁷",
  "8": "⁸",
  "9": "⁹",
};

/** Superscript minus, U+207B — distinct from the hyphen-minus used in the source. */
const SUPERSCRIPT_MINUS = "⁻";
/** Superscript fraction slash, U+141F "ᐟ" — separates a superscript numerator/denominator. */
const SUPERSCRIPT_SLASH = "ᐟ";

/** `x^2`, `x^-1`, `x^(-1)`, `x^(-1/2)` — parens optional, fraction optional. */
const CARET_EXPONENT = /\^(?:\((-?\d+(?:\/\d+)?)\)|(-?\d+(?:\/\d+)?))/g;

function toSuperscriptDigits(digits: string): string {
  return digits
    .split("")
    .map((ch) => SUPERSCRIPT_DIGITS[ch] ?? ch)
    .join("");
}

/** `"-1/2"` → `"⁻¹ᐟ²"`, `"2"` → `"²"`, `"-1"` → `"⁻¹"`. */
function superscriptExponent(raw: string): string {
  const [numerator, denominator] = raw.split("/");
  const negative = numerator.startsWith("-");
  const numeratorSup =
    (negative ? SUPERSCRIPT_MINUS : "") + toSuperscriptDigits(numerator.replace("-", ""));
  if (denominator === undefined) return numeratorSup;
  return `${numeratorSup}${SUPERSCRIPT_SLASH}${toSuperscriptDigits(denominator)}`;
}

/**
 * Converts caret exponents in a unit string to Unicode superscripts. Units
 * that are already Unicode (`"m⁻¹"`), plain (`"m/s"`, `"eV"`) or empty
 * (dimensionless) pass through unchanged. Never throws, even on malformed or
 * non-string input.
 */
export function formatUnit(unit: string): string {
  if (typeof unit !== "string" || unit === "") return typeof unit === "string" ? unit : "";
  try {
    return unit.replace(CARET_EXPONENT, (_match, withParens?: string, bare?: string) =>
      superscriptExponent(withParens ?? bare ?? ""),
    );
  } catch {
    return unit;
  }
}
